import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { Card } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { SECTION_LABEL } from './compact'

/**
 * A card whose body folds away under its header, with the chevron at the
 * header's right end. The body stays mounted while closed so the fold
 * animates both ways; its visibility flips only once the fold has finished,
 * which keeps a closed body out of the tab order.
 */
export function CollapsibleCard({
  title,
  meta,
  defaultOpen = false,
  className,
  testId,
  children
}: {
  title: string
  /** Shown before the chevron, e.g. a count. */
  meta?: React.ReactNode
  defaultOpen?: boolean
  className?: string
  testId?: string
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  const bodyId = React.useId()
  return (
    <Card className={cn('min-w-0 gap-0 p-0', className)} data-testid={testId}>
      <h2 className={SECTION_LABEL}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((value) => !value)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-xl p-3 text-left uppercase outline-none hover:text-ink-2 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <span className="min-w-0 flex-1">{title}</span>
          {meta ? (
            <span className="text-11 font-normal tracking-normal text-ink-3 normal-case">
              {meta}
            </span>
          ) : null}
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3.5 shrink-0 text-ink-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
              open && 'rotate-180'
            )}
          />
        </button>
      </h2>
      <div
        id={bodyId}
        data-open={open}
        className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
      >
        <div className="min-w-0 overflow-hidden">
          <div
            data-open={open}
            className="invisible px-3 pb-3 opacity-0 transition-[opacity,visibility] duration-200 ease-out motion-reduce:transition-none data-[open=true]:visible data-[open=true]:opacity-100"
          >
            {children}
          </div>
        </div>
      </div>
    </Card>
  )
}
